import {
  CircleUserRound,
  Heart,
  House,
  LayoutGrid,
  type LucideIcon,
  ShoppingCart,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { useSession } from "@/features/auth";
import { useCart } from "@/features/cart";
import { useCabinetAttention as useAttention } from "@/shared/api/generated/attention-controller/attention-controller";
import { cn } from "@/shared/lib/cn";
import { TAB_ROOT, type TabKey, TABS, tabTarget } from "../model/shell";

const ICONS: Record<TabKey, LucideIcon> = {
  home: House,
  catalog: LayoutGrid,
  cart: ShoppingCart,
  favorites: Heart,
  cabinet: CircleUserRound,
};

/**
 * The bottom tab bar on phones (D1): Главная · Каталог · Корзина · Избранное · Кабинет — the order and the words of
 * the existing mini-shop. Docked, 56 px + the home-indicator inset, 12 px labels, never hidden on scroll; steps
 * aside while the keyboard is open. A tap opens the last place of the tab; a tap on the active tab goes back to its
 * root, and at the root scrolls to the top.
 */
export function TabBar({ active }: { active: TabKey | undefined }) {
  const { t } = useTranslation("shop");
  const { count } = useCart();
  const needsAttention = useCabinetAttention();

  return (
    <nav
      data-tab-bar
      aria-label={t("tabs.label")}
      className="hide-with-keyboard fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface pb-safe lg:hidden"
    >
      <ul className="mx-auto flex h-14 max-w-xl">
        {TABS.map((tab) => (
          <li key={tab} className="flex-1">
            <TabLink
              tab={tab}
              active={tab === active}
              badge={tab === "cart" && count > 0 ? (count > 9 ? "9+" : String(count)) : undefined}
              badgeCount={tab === "cart" ? count : 0}
              dot={tab === "cabinet" && needsAttention}
            />
          </li>
        ))}
      </ul>
    </nav>
  );
}

function TabLink({
  tab,
  active,
  badge,
  badgeCount,
  dot,
}: {
  tab: TabKey;
  active: boolean;
  badge?: string;
  /** The real number behind a «9+» badge, for screen readers. */
  badgeCount: number;
  dot?: boolean;
}) {
  const { t } = useTranslation(["shop", "cart"]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const Icon = ICONS[tab];
  const label = t(`shop:tabs.${tab}`);

  return (
    <Link
      to={active ? TAB_ROOT[tab] : tabTarget(tab)}
      aria-current={active ? "page" : undefined}
      aria-label={badge ? `${label}: ${t("cart:count", { count: badgeCount })}` : undefined}
      onClick={(event) => {
        if (!active) return;
        event.preventDefault();
        if (pathname === TAB_ROOT[tab]) window.scrollTo({ top: 0, behavior: "smooth" });
        else void navigate(TAB_ROOT[tab]);
      }}
      className={cn(
        "flex h-full flex-col items-center justify-center gap-0.5 text-body-sm font-medium",
        active ? "text-brand-600" : "text-muted active:text-ink",
      )}
    >
      <span
        className={cn(
          "relative flex h-7 w-14 items-center justify-center rounded-full transition-colors duration-(--motion-base)",
          active && "bg-brand-50",
        )}
      >
        <Icon className={cn("size-6", active && "stroke-[2.25]")} aria-hidden />
        {badge && (
          <span
            key={badge}
            aria-hidden
            className="absolute top-0 right-2 flex h-4.5 min-w-4.5 animate-bump items-center justify-center rounded-full bg-brand-600 px-1 text-body-micro font-semibold text-white"
          >
            {badge}
          </span>
        )}
        {dot && (
          <span
            aria-hidden
            className="absolute top-0.5 right-3.5 size-2.5 rounded-full bg-danger ring-2 ring-surface"
          />
        )}
      </span>
      <span>{label}</span>
    </Link>
  );
}

/** The red dot on «Кабинет»: a payment is due within 3 days or is overdue. */
function useCabinetAttention(): boolean {
  const { status } = useSession();
  // the server decides what needs the customer (a payment due soon or overdue, an order waiting for them)
  const { data } = useAttention({
    query: { enabled: status === "authenticated", staleTime: 5 * 60_000 },
  });
  return data?.ca_has_attention ?? false;
}
